import { CommonModule } from '@angular/common';
import {
  ApplicationRef,
  Component,
  createComponent,
  EnvironmentInjector,
  signal,
} from '@angular/core';
import { Toast } from './toast';
import { ToastData, ToastPosition } from './toast.types';

interface ToastItem {
  data: ToastData;
  componentRef: any;
}

@Component({
  selector: 'lepi-toast-container',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="toast-container" [ngClass]="position">
      <div #toastHost></div>
    </div>
  `,
  styles: [
    `
      .toast-container {
        position: fixed;
        z-index: 999999;
      }

      .toast-container > * {
        pointer-events: auto;
      }

      .toast-top-left {
        top: 12px;
        left: 12px;
      }

      .toast-top-right {
        top: 12px;
        right: 12px;
      }

      .toast-top-center {
        top: 12px;
        left: 50%;
        transform: translateX(-50%);
      }

      .toast-bottom-left {
        bottom: 12px;
        left: 12px;
      }

      .toast-bottom-right {
        bottom: 12px;
        right: 12px;
      }

      .toast-bottom-center {
        bottom: 12px;
        left: 50%;
        transform: translateX(-50%);
      }
    `,
  ],
})
export class ToastContainer {
  position: ToastPosition = 'toast-top-right';
  toasts = signal<ToastItem[]>([]);

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector
  ) {}

  addToast(data: ToastData, hostElement: HTMLElement) {
    const componentRef = createComponent(Toast, {
      environmentInjector: this.injector,
      hostElement: document.createElement('div'),
    });

    componentRef.setInput('toastData', data);
    componentRef.instance.removed.subscribe((id: string) => {
      this.removeToast(id);
    });

    // Add animation class
    componentRef.location.nativeElement.classList.add('toast-fade-in');
    componentRef.location.nativeElement.classList.add(`toast-${data.type}`);

    hostElement.appendChild(componentRef.location.nativeElement);
    this.appRef.attachView(componentRef.hostView);

    this.toasts.update(toasts => [...toasts, { data, componentRef }]);

    return data.id;
  }

  removeToast(id: string) {
    const toastItem = this.toasts().find(t => t.data.id === id);
    if (!toastItem) return;

    const element = toastItem.componentRef.location.nativeElement;
    element.classList.remove('toast-fade-in');
    element.classList.add('toast-fade-out');

    setTimeout(() => {
      this.appRef.detachView(toastItem.componentRef.hostView);
      toastItem.componentRef.destroy();
      this.toasts.update(toasts => toasts.filter(t => t.data.id !== id));
    }, 300);
  }

  clear() {
    this.toasts().forEach(toast => {
      this.removeToast(toast.data.id);
    });
  }
}
